import React, { useState, useCallback } from 'react'
import { Box, IconButton, Button, Tooltip } from '@mui/material'
import { ThumbUpOutlined, ThumbDownOutlined, ThumbUp, ThumbDown } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { gql } from 'graphql-request'
import { gqlMutate } from '@hapana/shared/web/services/requests.service'
import { useSupportAssistant } from '../support-assistant.hooks'

const provideFeedbackMutation = gql`
  mutation ProvideSupportMessageFeedback($input: SupportMessageFeedbackInput!) {
    provideSupportMessageFeedback(input: $input) {
      supportChatMessageId
      feedback
    }
  }
`

interface MessageFeedbackProps {
  messageId: string
  initialFeedback?: boolean | null
  onEmailSupportClick: () => void
  onFeedbackChange?: (feedback: boolean | null) => void
}

export function MessageFeedback({
  messageId,
  initialFeedback = null,
  onEmailSupportClick,
  onFeedbackChange,
}: MessageFeedbackProps) {
  const { t } = useTranslation('core')
  const { chatId } = useSupportAssistant()
  const [feedback, setFeedback] = useState<boolean | null>(initialFeedback)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleFeedbackClick = useCallback(
    async (isPositive: boolean) => {
      // Toggle: clicking the same button clears feedback
      const newFeedback = feedback === isPositive ? null : isPositive

      setFeedback(newFeedback)
      setIsSubmitting(true)

      try {
        if (chatId && newFeedback !== null) {
          await gqlMutate({
            mutation: provideFeedbackMutation,
            variables: {
              input: {
                supportChatId: chatId,
                supportChatMessageId: messageId,
                feedback: newFeedback,
              },
            },
            options: { isV1: false },
          })
        }
        onFeedbackChange?.(newFeedback)
      } catch (error) {
        console.error('Failed to submit feedback:', error)
        setFeedback(feedback)
      } finally {
        setIsSubmitting(false)
      }
    },
    [feedback, messageId, chatId, onFeedbackChange]
  )

  return (
    <Box
      data-testid="message-feedback"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mt: 1,
      }}
    >
      {/* Thumbs Up */}
      <Tooltip title={t('supportAssistant.feedback.helpful')}>
        <IconButton
          size="small"
          onClick={() => handleFeedbackClick(true)}
          disabled={isSubmitting}
          data-testid="feedback-thumbs-up"
          aria-label={t('supportAssistant.feedback.helpful')}
          sx={{
            color: feedback === true ? 'primary.main' : 'grey.500',
            border: '1px solid',
            borderColor: feedback === true ? 'primary.main' : 'grey.300',
            borderRadius: '50%',
          }}
        >
          {feedback === true ? <ThumbUp fontSize="small" /> : <ThumbUpOutlined fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Thumbs Down */}
      <Tooltip title={t('supportAssistant.feedback.notHelpful')}>
        <IconButton
          size="small"
          onClick={() => handleFeedbackClick(false)}
          disabled={isSubmitting}
          data-testid="feedback-thumbs-down"
          aria-label={t('supportAssistant.feedback.notHelpful')}
          sx={{
            color: feedback === false ? 'error.main' : 'grey.500',
            border: '1px solid',
            borderColor: feedback === false ? 'error.main' : 'grey.300',
            borderRadius: '50%',
          }}
        >
          {feedback === false ? (
            <ThumbDown fontSize="small" />
          ) : (
            <ThumbDownOutlined fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      {/* Email Support Button */}
      <Button
        variant="text"
        size="small"
        onClick={onEmailSupportClick}
        data-testid="feedback-email-support"
        sx={{
          ml: 1.5,
          color: 'text.secondary',
          textTransform: 'none',
          fontSize: '0.813rem',
          minWidth: 'auto',
          px: 1,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {t('supportAssistant.feedback.emailSupport')}
      </Button>
    </Box>
  )
}
